import React, { useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { InlineFieldRow } from '../../../../ui/_fields/InlineFieldRow/InlineFieldRow';
import { NumberField } from '../../../../ui/_fields/NumberField/NumberField';
import { TextField } from '../../../../ui/_fields/TextField/TextField';
import { OfferDateTimeField } from '../../OfferDateTimeField';
import { usePersistedDisplayTimeZone } from '../../usePersistedDisplayTimeZone';
import { useTimeZoneOptions } from '../../useTimeZoneOptions';
import { utcToZonedWallClock, zonedWallClockToUtc } from '../../offerTimeZoneConversion';
import { FieldTooltipBadge } from './FieldTooltipBadge';
import { PopupImagePreview } from './PopupImagePreview';
import { PositionsTable } from './PositionsTable';
import {
  clearFieldsForSchedulingMode, OfferFormValues, SchedulingType,
} from './offerFormLogic';
import { useOfferFormFields } from './useOfferFormFields';

interface OfferFormProps {
  offerFormValues: OfferFormValues
  isLoading: boolean
  isEditing: boolean
  onSubmit: (values: OfferFormValues) => void
  onCancel: () => void
  submitError: string
  onDirtyChange?: (dirty: boolean) => void
}

export function OfferForm(props: OfferFormProps) {
  const {
    offerFormValues, isLoading, isEditing, onSubmit, onCancel, submitError, onDirtyChange,
  } = props;

  const {
    handleSubmit, handleChange, setFieldValue, setValues, values, errors, touched, dirty,
    fieldError, positionError, addPosition, removePosition, positionKeys,
    popupImageFileError, handlePopupImageFileChange,
  } = useOfferFormFields(offerFormValues, onSubmit, submitError);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const { timeZone, setTimeZone } = usePersistedDisplayTimeZone();
  const timeZoneOptions = useTimeZoneOptions();

  const handleSchedulingTypeChange = (schedulingType: SchedulingType) => {
    setValues((prevValues) => clearFieldsForSchedulingMode(prevValues, schedulingType));
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <div className="d-flex align-items-center">
        <h5 className="mb-0 mr-3">IDENTITY</h5>
        <Form.Check
          custom
          id="offer-active"
          label="Active"
          name="active"
          checked={values.active}
          onChange={handleChange}
        />
      </div>
      <Row className="mb-3">
        <Col md={6}>
          <TextField
            inline
            label="Display name"
            name="display_name"
            value={values.display_name}
            onChange={handleChange}
            error={(touched.display_name ? errors.display_name : '') || fieldError('display_name')}
          />
        </Col>
        <Col md={6}>
          {isEditing ? (
            <InlineFieldRow
              label={<FieldTooltipBadge id="identifier-readonly-tooltip" title="ID" content="This identifier is permanent and cannot be changed.">ID</FieldTooltipBadge>}
            >
              <Form.Control type="text" value={values.identifier} readOnly disabled />
            </InlineFieldRow>
          ) : (
            <TextField
              inline
              label={(
                <FieldTooltipBadge
                  id="identifier-tooltip"
                  title="ID"
                  content="Short unique key, set once on save and never editable later. Lowercase letters, digits and hyphens only."
                >
                  ID
                </FieldTooltipBadge>
              )}
              name="identifier"
              value={values.identifier}
              onChange={handleChange}
              error={fieldError('identifier')}
            />
          )}
        </Col>
      </Row>

      <h5>SCHEDULING</h5>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Check
              custom
              type="radio"
              id="scheduling-type-everyone"
              name="scheduling_type"
              label="For everyone"
              className="mt-3"
              checked={values.scheduling_type === 'everyone'}
              onChange={() => handleSchedulingTypeChange('everyone')}
            />
            <Form.Check
              custom
              type="radio"
              id="scheduling-type-new-users"
              name="scheduling_type"
              label="For new users"
              className="mt-3"
              checked={values.scheduling_type === 'new_users'}
              onChange={() => handleSchedulingTypeChange('new_users')}
            />
            <Form.Check
              custom
              type="radio"
              id="scheduling-type-open-ended"
              name="scheduling_type"
              label="Open (no end date)"
              className="mt-3"
              checked={values.scheduling_type === 'open_ended'}
              onChange={() => handleSchedulingTypeChange('open_ended')}
            />
          </Form.Group>
        </Col>
        <Col md={5}>
          {values.scheduling_type !== 'new_users' && (
            <InlineFieldRow label="Show times in" htmlFor="offer-form-time-zone">
              <Form.Control
                id="offer-form-time-zone"
                as="select"
                value={timeZone}
                onChange={(event) => setTimeZone(event.target.value)}
              >
                {timeZoneOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Form.Control>
            </InlineFieldRow>
          )}
          {values.scheduling_type !== 'new_users' && (
            <OfferDateTimeField
              inline
              label="Start date/time"
              value={utcToZonedWallClock(values.start_time, timeZone)}
              onChange={(wallClock) => setFieldValue('start_time', wallClock ? zonedWallClockToUtc(wallClock, timeZone) : '')}
              error={touched.start_time ? errors.start_time : ''}
            />
          )}
          {values.scheduling_type === 'everyone' && (
            <OfferDateTimeField
              inline
              label="End date/time"
              value={utcToZonedWallClock(values.end_time, timeZone)}
              onChange={(wallClock) => setFieldValue('end_time', wallClock ? zonedWallClockToUtc(wallClock, timeZone) : '')}
              isClearable
              error={touched.end_time ? errors.end_time : ''}
            />
          )}
          {values.scheduling_type === 'new_users' && (
            <NumberField
              inline
              label={(
                <FieldTooltipBadge
                  id="new-player-window-tooltip"
                  title="New-player window (hours)"
                  content="Counted per player, from their account's registration."
                >
                  New-player window (hours)
                </FieldTooltipBadge>
              )}
              name="new_player_window_hours"
              value={values.new_player_window_hours}
              onChange={handleChange}
              error={touched.new_player_window_hours ? errors.new_player_window_hours : ''}
              min={1}
              step={1}
            />
          )}
        </Col>
      </Row>

      <h5>PRODUCT</h5>
      <Row className="mb-3">
        <Col md={6}>
          <TextField
            inline
            label={(
              <FieldTooltipBadge
                id="product-slug-tooltip"
                title="Product identifier"
                content="Paste the exact product identifier from the App Store Connect or Google Play console listing."
              >
                Product identifier
              </FieldTooltipBadge>
            )}
            name="product_slug"
            value={values.product_slug}
            onChange={handleChange}
            error={fieldError('product_slug')}
          />
        </Col>
      </Row>

      <h5>STORY POSITION</h5>
      <Row className="mb-3">
        <Col md={12}>
          <PositionsTable
            positions={values.positions}
            positionKeys={positionKeys}
            onChange={handleChange}
            positionError={positionError}
            onAddPosition={addPosition}
            onRemovePosition={removePosition}
          />
        </Col>
      </Row>

      <h5>APPEARANCE</h5>
      <Row className="mb-3">
        <Col md="auto">
          <PopupImagePreview
            imageURL={values.popup_image_url}
            top={values.appearance_top}
            width={values.appearance_width}
            height={values.appearance_height}
          />
        </Col>
        <Col>
          <Form.Group>
            <Form.Label>Popup image</Form.Label>
            <Form.Control
              type="file"
              name="popup_image"
              aria-label="Popup image"
              accept="image/png, image/jpeg"
              onChange={handlePopupImageFileChange}
              isInvalid={!!(popupImageFileError || (touched.popup_image_url ? errors.popup_image_url : '') || fieldError('popup_image_url'))}
            />
            <Form.Text>JPEG or PNG, up to 5 MB.</Form.Text>
            <Form.Control.Feedback type="invalid">
              {popupImageFileError || (touched.popup_image_url ? errors.popup_image_url : '') || fieldError('popup_image_url')}
            </Form.Control.Feedback>
          </Form.Group>
          <NumberField
            inline
            label="Vertical position (%)"
            name="appearance_top"
            value={values.appearance_top}
            onChange={handleChange}
            error={(touched.appearance_top ? errors.appearance_top : '') || fieldError('appearance_top')}
            min={0}
            max={100}
            step="any"
          />
          <NumberField
            inline
            label="Width (%)"
            name="appearance_width"
            value={values.appearance_width}
            onChange={handleChange}
            error={(touched.appearance_width ? errors.appearance_width : '') || fieldError('appearance_width')}
            min={0}
            max={100}
            step="any"
          />
          <NumberField
            inline
            label="Height (%)"
            name="appearance_height"
            value={values.appearance_height}
            onChange={handleChange}
            error={(touched.appearance_height ? errors.appearance_height : '') || fieldError('appearance_height')}
            min={0}
            max={100}
            step="any"
          />
        </Col>
      </Row>

      <Row className="mr-4 mb-3 justify-content-end">
        <Col sm="auto">
          <Button type="button" variant="secondary" disabled={isLoading} onClick={onCancel}>
            Cancel
          </Button>
        </Col>
        <Col sm="auto">
          <Button type="submit" variant="primary" disabled={isLoading}>
            Save offer
          </Button>
        </Col>
      </Row>
    </Form>
  );
}
